export const featurePages = [
  {
    slug: 'qr-code-menu',
    icon: '📱',
    title: 'QR Code Menu Generator',
    shortDescription: 'Put your menu in every guest’s hand instantly with branded, ready-to-print QR codes.',
    overview:
      'Launch table-ready QR menus in minutes and keep them always up to date. Guests scan once and see your latest menu, while your team stays consistent across all tables and service points.',
    capabilities: [
      'Create branded QR codes for every menu and location',
      'Keep the same code while menu updates appear automatically',
      'Use ready-made print layouts for tables, counters, and walls',
      'Generate many QR codes at once for faster rollout',
      'Download print-ready files for your staff or print shop',
    ],
    useCases: [
      'Restaurants replacing printed menus quickly',
      'Cafe chains managing multiple locations',
      'Seasonal menus that change frequently',
      'Events and pop-ups needing fast QR setup',
    ],
  },
  {
    slug: 'menu-builder',
    icon: '🎨',
    title: 'Drag-and-Drop Menu Builder',
    shortDescription: 'Build and rearrange your menu visually in seconds, without design or technical skills.',
    overview:
      'Create menus the way guests browse: clear sections, appealing dish cards, and simple ordering choices. Update content any time and keep your menu aligned with daily service.',
    capabilities: [
      'Reorder categories and dishes with simple drag and drop',
      'Show dishes with photos, descriptions, and key dietary details',
      'Offer different sizes and extras to increase average order value',
      'Set menu sections by time of day (breakfast, lunch, dinner)',
      'Keep unavailable sections out of the ordering flow',
    ],
    useCases: [
      'Fast daily specials and seasonal updates',
      'Menus with complex size and modifier options',
      'Venues with time-limited categories (breakfast, lunch, dinner, late-night)',
      'Non-technical staff managing menu content',
      'Multi-category menus for full-service restaurants',
    ],
  },
  {
    slug: 'online-ordering',
    icon: '🛒',
    title: 'Online Ordering & Cart',
    shortDescription: 'Turn your menu into a full ordering channel for dine-in, pickup, and room service.',
    overview:
      'Let guests order smoothly from their phone and give your staff a faster service flow. Support flexible payment preferences, pickup scenarios, and live order progress in one place.',
    capabilities: [
      'Fast checkout with all essential customer details',
      'Support cash, card, and room-charge payment flows',
      'Allow split payments for groups and shared bills',
      'Give guests live order status updates after checkout',
      'Run self-pickup with your own service schedule',
      'Sync order lifecycle updates with efood and Wolt and keep statuses aligned',
      'Manage outlet availability (OPEN, CLOSED, CLOSED_UNTIL, CHECKIN) from one dashboard',
      'Charge a sunbed cost only once per guest, together with their first order of the day',
    ],
    useCases: [
      'Dine-in scan-to-order at the table',
      'Takeaway and self pick-up with scheduled time slots',
      'Split bills with per-item partial payments and mixed payment types',
      'Hybrid operations with third-party delivery integrations',
      'Room service ordering for hotels with room charge payment',
      'High-volume venues needing daily order number resets',
    ],
  },
  {
    slug: 'third-party-integrations',
    icon: '🔗',
    title: '3rd-Party Integrations',
    shortDescription: 'Connect with efood and Wolt from one place and keep your menu, orders, and statuses aligned.',
    overview:
      'Operate marketplace channels without switching systems. Configure integrations per location, receive partner orders, and keep status updates synchronized both ways for smoother daily operations.',
    capabilities: [
      'Receive orders from efood and Wolt webhooks and map items by SKU',
      'Sync local order status updates back to partner platforms',
      'Manage efood outlet availability (OPEN, CLOSED, CLOSED_UNTIL, CHECKIN)',
      'Store credentials per location for safer multi-branch operations',
      'Track incoming and outgoing integration events with structured logs',
      'Sync local menus to Wolt with a dedicated menu sync flow',
    ],
    useCases: [
      'Restaurants running both direct and marketplace order channels',
      'Multi-location operations with separate partner credentials per branch',
      'Teams that need one dashboard for efood and Wolt monitoring',
      'Operations requiring reliable status alignment to reduce support calls',
      'Brands scaling delivery volume while keeping kitchen flow predictable',
    ],
  },
  {
    slug: 'payments-and-tax-billing',
    icon: '💳',
    title: 'Payments & Tax Billing',
    shortDescription: 'Collect payments your way and keep billing compliant with confidence.',
    overview:
      'Handle payment and billing from one place so your team works faster and with fewer mistakes. Keep operations clear for staff, customers, and accounting, even during busy hours.',
    capabilities: [
      'Offer online card, in-person card, cash, and room-charge options',
      'Keep payment confirmations clear and reliable',
      'Control when billing is sent automatically or manually',
      'Track billing status per order and retry failed submissions easily',
      'Keep receipt and invoice details organized for accounting',
    ],
    useCases: [
      'Businesses that need both online and in-person card payment collection',
      'Greek market operations requiring AADE myDATA submission',
      'Hotels offering room charge as a payment option',
      'Teams that require manual control over billing finalization',
      'Venues that combine waiter-confirm and online prepayment flows',
    ],
  },
  {
    slug: 'pay-at-table',
    icon: '💰',
    title: 'Pay at Table',
    shortDescription: 'Let guests settle the bill from their phone, without waiting for the check.',
    overview:
      'Speed up table turnover by allowing customers to pay as soon as they are ready. It is smoother for guests and reduces back-and-forth for your floor team.',
    capabilities: [
      'Guests pay the remaining balance in a few taps',
      'Reduce waiting time at the end of the meal',
      'Avoid confusion with clear paid vs unpaid amounts',
      'Fit naturally into your existing order flow',
      'Give customers a familiar, mobile-friendly payment journey',
    ],
    useCases: [
      'Dine-in restaurants letting guests pay when ready instead of waiting for the bill',
      'Hotels enabling room-to-online payment conversion for restaurant charges',
      'Bars and cafes offering frictionless tab settlement',
      'High-volume venues reducing waiter trips for payment collection',
    ],
  },
  {
    slug: 'multi-language-menus',
    icon: '🌍',
    title: 'Multi-Language Menus',
    shortDescription: 'Serve international guests with a menu they instantly understand.',
    overview:
      'Show your menu in multiple languages and make ordering easier for travelers and international customers. Keep translations consistent while staying in control of your brand voice.',
    capabilities: [
      'Offer your menu in the languages your guests need',
      'Let customers switch language directly on the menu page',
      'Translate menu content faster and edit anything manually',
      'Keep dish names, descriptions, and key details clear',
      'Use one menu source while publishing in many languages',
    ],
    useCases: [
      'Tourist-heavy destinations and international airports',
      'Hotels serving guests from multiple countries',
      'Restaurant chains operating across borders',
      'Beach bars and resorts with diverse clientele',
      'Businesses needing instant menu translations without hiring translators',
    ],
  },
  {
    slug: 'table-management',
    icon: '🍽️',
    title: 'Table & Waiter Call Management',
    shortDescription: 'Run floor service with smarter table setup and faster guest support.',
    overview:
      'Organize table operations so staff can respond faster and guests enjoy a smoother experience. Keep service rules clear by area, shift, and table type.',
    capabilities: [
      'Set up and manage tables quickly per location',
      'Link each table to the right menu experience',
      'See table availability at a glance',
      'Control ordering hours by seating area',
      'Allow guests to call staff directly from the menu',
    ],
    useCases: [
      'Busy dine-in restaurants needing faster table turns',
      'Large venues with multiple zones and floors',
      'Venues with split operating hours (lunch + dinner service)',
      'Takeaway and self pick-up counters with scheduled availability',
      'Outdoor seating areas where staff visibility is limited',
      'Hotels with poolside and rooftop service points',
    ],
  },
  {
    slug: 'restaurant-analytics',
    icon: '📊',
    title: 'Restaurant Menu Analytics',
    shortDescription: 'See what sells, when it sells, and where to grow revenue faster.',
    overview:
      'Turn daily activity into practical business decisions. Spot your best-performing items, busiest periods, and highest-value areas so you can improve sales with confidence.',
    capabilities: [
      'Track customer activity from menu view to completed order',
      'Identify top dishes and low performers quickly',
      'See revenue trends by day, table, and seating area',
      'Understand peak service hours for better staffing',
      'Compare performance across locations and periods',
    ],
    useCases: [
      'Data-driven menu optimization and pricing',
      'Campaign and promotion impact measurement',
      'Multi-location performance benchmarking',
      'Identifying underperforming items to retire',
    ],
  },
  {
    slug: 'restaurant-onboarding',
    icon: '🚀',
    title: 'Restaurant Onboarding Workflow',
    shortDescription: 'Go live quickly with a guided setup built for busy restaurant teams.',
    overview:
      'Start fast without getting stuck in setup details. From branding to menu launch, everything is guided so your team can begin taking orders sooner.',
    capabilities: [
      'Follow a simple step-by-step launch flow',
      'Set up business location and contact details in minutes',
      'Apply your logo, colors, and style quickly',
      'Import existing menus to save time',
      'Build and publish your first menu with confidence',
    ],
    useCases: [
      'New restaurant launches going digital from day one',
      'Migrating from paper menus or spreadsheets',
      'Franchise onboarding with standardized playbooks',
      'Pop-up events needing rapid menu deployment',
    ],
  },
  {
    slug: 'custom-branding',
    icon: '🎨',
    title: 'Custom Menu Branding',
    shortDescription: 'Make your online menu look and feel like your brand from the first glance.',
    overview:
      'Create a branded experience that feels premium and recognizable. Keep your menu design aligned with your venue style so customers trust it instantly.',
    capabilities: [
      'Apply your logo, colors, and visual style in minutes',
      'Keep one consistent identity across customer touchpoints',
      'Fine-tune menu look for premium presentation',
      'Preview changes before publishing',
      'Adapt styling per menu when needed',
    ],
    useCases: [
      'Restaurants matching digital menus to their interior design',
      'Multi-brand hotel groups with different identities per venue',
      'Seasonal menu themes (holiday colors, summer vibes)',
      'Franchise operations maintaining brand consistency',
    ],
  },
  {
    slug: 'csv-import-export',
    icon: '📄',
    title: 'CSV Import & Export',
    shortDescription: 'Move menu data in and out quickly to save hours of manual work.',
    overview:
      'Bring your existing menus over fast and keep full ownership of your data. Perfect for large updates, backups, and collaboration with your team.',
    capabilities: [
      'Import many items at once instead of typing one by one',
      'Export your menu whenever you need a backup or review',
      'Use ready templates to reduce formatting mistakes',
      'Make seasonal or campaign updates in bulk',
      'Combine spreadsheet speed with visual editing',
    ],
    useCases: [
      'Migrating from spreadsheets or another menu platform',
      'Franchise rollouts with a standardized menu template',
      'Seasonal menu overhauls replacing all items at once',
      'Backup and restore workflows for data safety',
    ],
  },
  {
    slug: 'employee-management',
    icon: '👥',
    title: 'Employee Management & Shift Reports',
    shortDescription: 'Coordinate your team better with clear roles, daily performance, and shift visibility.',
    overview:
      'Keep floor operations organized with clear assignments and transparent results. Managers can track performance fairly and support staff where it matters most.',
    capabilities: [
      'Assign staff by area and shift for smoother service',
      'Give each employee a focused view of responsibilities',
      'Track daily performance with practical metrics',
      'Review shift results quickly for reconciliation',
      'Spot training opportunities and top performers',
    ],
    useCases: [
      'Restaurants tracking waiter performance and tip reconciliation',
      'Hotels assigning staff to different service areas (pool, restaurant, room service)',
      'Beach bars with rotating shift schedules across zones',
      'Cafe chains benchmarking employee efficiency across locations',
    ],
  },
  {
    slug: 'kitchen-display-system',
    icon: '🖥️',
    title: 'Kitchen Display System (KDS)',
    shortDescription: 'Keep your kitchen in sync with clear screens, priorities, and faster handoffs.',
    overview:
      'Run a calmer, faster kitchen with better visibility for every station. Teams can focus on what matters now, reduce mistakes, and improve delivery timing.',
    capabilities: [
      'Show each station exactly the items it needs to prepare',
      'Highlight urgent orders so teams can prioritize correctly',
      'Track item completion clearly to reduce misses',
      'Coordinate handoff between kitchen and service smoothly',
      'View all active orders in one operational screen',
    ],
    useCases: [
      'Restaurant kitchens routing orders to the right prep station',
      'Bars splitting food and drink orders across kitchen and bar staff',
      'Hotels managing room service across multiple kitchen stations',
      'High-volume operations tracking order timing and urgency',
    ],
  },
  {
    slug: 'customer-management',
    icon: '👤',
    title: 'Customer Management',
    shortDescription: 'Build stronger customer relationships with history, preferences, and faster reordering.',
    overview:
      'Turn everyday orders into valuable customer insight. Make repeat checkout faster, deliver better service, and encourage loyalty over time.',
    capabilities: [
      'Keep a clear profile and order history for each customer',
      'Find returning customers quickly during service',
      'See who orders most and spends most over time',
      'Speed up checkout with prefilled customer details',
      'Support better service through customer context',
    ],
    useCases: [
      'Restaurants building a loyal customer base from repeat orders',
      'Hotels tracking guest ordering patterns across venues',
      'Cafes offering faster checkout for regulars',
      'Businesses analyzing customer lifetime value and order frequency',
    ],
  },
  {
    slug: 'inventory-tracking',
    icon: '📦',
    title: 'Inventory & Stock Tracking',
    shortDescription: 'Stay in control of stock and avoid disappointing guests with unavailable items.',
    overview:
      'Keep menu availability accurate throughout the day so customers only see what they can actually order. Reduce waste, protect margins, and make service decisions faster.',
    capabilities: [
      'Track stock by item with simple daily controls',
      'Update quantities quickly during busy shifts',
      'Reflect sold items immediately in the ordering experience',
      'Restore availability easily when orders change',
      'Prevent overselling and last-minute customer frustration',
    ],
    useCases: [
      'Daily specials with limited quantities (sell out alerts)',
      'Food trucks with constrained ingredient supplies',
      'Bakeries and cafes tracking fresh pastry batches',
      'Events and pop-ups with pre-portioned servings',
    ],
  },
  {
    slug: 'receipt-printing',
    icon: '🖨️',
    title: 'Receipt & Order Printing',
    shortDescription: 'Print receipts and order tickets exactly where your team needs them.',
    overview:
      'Make order handling smoother with reliable printing across cashier and kitchen points. Keep each team focused by sending the right printout to the right place automatically.',
    capabilities: [
      'Print customer receipts and internal tickets quickly',
      'Route prints by location, area, or workflow',
      'Support both on-site and cloud printing setups',
      'Enable automatic printing when orders are confirmed',
      'Reduce manual printing steps for staff',
    ],
    useCases: [
      'Restaurants printing kitchen tickets on order confirmation',
      'Cashier stations printing customer receipts per seating area',
      'Multi-location businesses with centralized cloud print management',
      'Hotels routing receipts to different service point printers',
    ],
  },
  {
    slug: 'menu-widget-embed',
    icon: '🧩',
    title: 'Website Widget Embed',
    shortDescription: 'Embed your live menu on your website and keep it always up to date.',
    overview:
      'Turn your website into a direct ordering and discovery channel without extra maintenance. Publish once, then keep content fresh automatically as your menu evolves.',
    capabilities: [
      'Generate website embed code in seconds',
      'Choose which menu to display for each brand or location',
      'Match widget style to your website look',
      'Preview before publishing',
      'Keep website menu content updated automatically',
    ],
    useCases: [
      'Restaurant websites embedding the latest menu without manual updates',
      'Hotel websites showing room service or restaurant menus in-page',
      'Landing pages and campaign pages with embedded ordering menus',
      'Agencies managing menu embeds across multiple client sites',
    ],
  },
  {
    slug: 'delivery-zones',
    icon: '📍',
    title: 'Delivery Zones & Address Verification',
    shortDescription: 'Deliver only where you can serve well, with clear zones and fewer failed orders.',
    overview:
      'Set clear delivery boundaries so customers know immediately if service is available. Reduce confusion, avoid rejected orders, and improve delivery reliability.',
    capabilities: [
      'Draw and manage delivery areas on a map',
      'Check customer addresses before they place an order',
      'Show coverage clearly to avoid misunderstandings',
      'Support both delivery and self-pickup flows',
      'Help staff plan routes with better order context',
    ],
    useCases: [
      'Restaurants offering delivery within a specific radius or neighborhood',
      'Ghost kitchens defining precise coverage zones per brand',
      'Hotels with room service and poolside delivery boundaries',
      'Catering companies validating addresses before accepting orders',
      'Food trucks broadcasting their current delivery zone',
    ],
  },
  {
    slug: 'reservations-and-table-board',
    icon: '🗓️',
    title: 'Reservations & Live Table Board',
    shortDescription:
      'Take bookings with confidence and run the floor from a single live board that shows every table, order, and reservation in real time.',
    overview:
      'Give your host stand the tools to plan the day and your managers a single screen to run service. Take reservations with guaranteed table holds, close tables for private events, and watch the floor update live as orders are placed, kitchen updates roll in, and guest status changes — no refresh required.',
    capabilities: [
      'Create, edit, and cancel table reservations with customer name, guests, time, and optional contact details',
      'Reserve one or several specific tables per booking, or mark tables as closed for private events',
      'Automatic hold window: tables flip to "reserved" 30 minutes before the booking and release 2 hours after, so the floor is never blocked all day',
      'Dedicated "Reservations of the day" page with clear time sorting and quick edit/delete',
      'Live Table Board with a KDS-style view: every table grouped by seating area with color-coded status (available, occupied, reserved, closed, inactive)',
      'Real-time WebSocket updates — new orders and status changes appear instantly with a visual flash on the affected table',
      'Instant status switching from the board: tap a table and change it to available, occupied, reserved, closed, or inactive in one click',
      'At-a-glance per-table detail: open orders for the day with totals, server, payment status, plus today\'s reservations touching that table',
      'Compact stats strip showing totals, available / occupied / reserved / closed counts, open orders, pending waiter calls, and reservations today',
      'Filters by status and by seating area, plus a "LIVE / OFFLINE" indicator showing WebSocket health',
      'Public menu access and waiter calls are automatically blocked for closed or inactive tables, so nothing slips through',
      'Timezone-accurate bookings (Europe/Athens) — the time you pick is the time the guest sees, with no UTC surprises',
    ],
    useCases: [
      'Restaurants that take phone reservations and need a single screen showing today\'s bookings',
      'Fine-dining venues that want to close specific tables for private events while keeping the rest of the floor bookable',
      'Busy bistros where managers need real-time visibility of open orders and table status from one dashboard',
      'Hotels and resorts with multiple seating areas (pool, restaurant, rooftop) needing unified floor control',
      'Teams running pre-service setup: seeing who is arriving, at what time, and on which tables',
      'Staff handling walk-ins and needing to instantly mark a table as occupied or released without menu navigation',
      'Owners monitoring the floor from their office or phone with live updates instead of manual refreshes',
    ],
  },
  {
    slug: 'android-app',
    icon: '📲',
    title: 'Android App for Employees & Owners',
    shortDescription: 'Run your operation from your phone with the Android app for owners and staff.',
    overview:
      'Stay in control even away from the counter. Teams can react faster, manage service in real time, and keep operations moving from anywhere.',
    capabilities: [
      'Manage orders and status updates while on the move',
      'Handle shifts and daily responsibilities from one app',
      'Respond quickly to guest service requests',
      'Monitor tables and seating activity in real time',
      'Review sales and team performance anytime',
    ],
    useCases: [
      'Waiters managing orders and waiter calls from their phone during shifts',
      'Owners monitoring sales and employee activity away from the desk',
      'Floor managers tracking table occupancy and coordinating seating',
      'Mobile staff at events, food trucks, and outdoor venues',
    ],
  },
];

export function getFeatureBySlug(slug) {
  return featurePages.find((item) => item.slug === slug) || null;
}

const FEATURE_TRANSLATIONS_EL = {
  'qr-code-menu': {
    title: 'Δημιουργία QR Code Μενού',
    shortDescription: 'Βάλτε το μενού σας στα χέρια κάθε πελάτη άμεσα με branded QR έτοιμα για εκτύπωση.',
    overview: 'Στήστε QR μενού σε λίγα λεπτά και κρατήστε το περιεχόμενο πάντα ενημερωμένο. Οι πελάτες σκανάρουν μία φορά και βλέπουν πάντα την τελευταία έκδοση.',
    capabilities: [
      'Δημιουργία branded QR για κάθε μενού και τοποθεσία',
      'Το ίδιο QR παραμένει ενεργό όσο εσείς ανανεώνετε το μενού',
      'Έτοιμες μορφές εκτύπωσης για τραπέζια, πάγκους και βιτρίνες',
      'Μαζική δημιουργία QR για γρήγορο rollout',
      'Αρχεία έτοιμα για άμεση εκτύπωση',
    ],
    useCases: ['Εστιατόρια που αντικαθιστούν έντυπα μενού', 'Αλυσίδες cafe με πολλά σημεία', 'Συχνές αλλαγές σε εποχικά μενού', 'Events και pop-ups'],
  },
  'menu-builder': {
    title: 'Drag-and-Drop Menu Builder',
    shortDescription: 'Χτίστε και αναδιατάξτε το μενού οπτικά σε δευτερόλεπτα, χωρίς τεχνικές γνώσεις.',
    overview: 'Δημιουργήστε ένα μενού που πουλάει καλύτερα: καθαρές κατηγορίες, σωστή παρουσίαση πιάτων και εύκολη εμπειρία παραγγελίας για τον πελάτη.',
    capabilities: [
      'Αναδιάταξη κατηγοριών και πιάτων με drag-and-drop',
      'Παρουσίαση πιάτων με εικόνα, περιγραφή και βασικές διατροφικές πληροφορίες',
      'Επιλογές μεγεθών και extras για αύξηση μέσης απόδειξης',
      'Ορισμός κατηγοριών ανά ώρα (πρωινό, μεσημεριανό, βραδινό)',
      'Κρατάτε εκτός παραγγελίας ό,τι δεν είναι διαθέσιμο',
    ],
    useCases: ['Καθημερινές αλλαγές σε specials', 'Μενού με σύνθετες επιλογές', 'Χώροι με κατηγορίες ανά ώρα (πρωινό, μεσημεριανό, βραδινό)', 'Χρήστες χωρίς τεχνικές γνώσεις', 'Πλήρη πολυκατηγορικά μενού'],
  },
  'online-ordering': {
    title: 'Online Παραγγελίες & Καλάθι',
    shortDescription: 'Μετατρέψτε το μενού σας σε πλήρες κανάλι παραγγελιών για dine-in, παραλαβή και room service.',
    overview: 'Δώστε στους πελάτες σας εύκολη παραγγελία από κινητό και στην ομάδα σας καθαρή, γρήγορη ροή εξυπηρέτησης.',
    capabilities: [
      'Γρήγορο checkout με όλα τα βασικά στοιχεία πελάτη',
      'Υποστήριξη μετρητών, κάρτας και χρέωσης δωματίου',
      'Μερικές πληρωμές για παρέες και split λογαριασμούς',
      'Live ενημέρωση κατάστασης παραγγελίας για τον πελάτη',
      'Self pick-up με δικό σας πρόγραμμα λειτουργίας',
      'Χρέωση ξαπλώστρας μόνο μία φορά ανά πελάτη, μαζί με την πρώτη παραγγελία της ημέρας',
    ],
    useCases: ['Scan-to-order στο τραπέζι', 'Takeaway και self pick-up με ωράριο', 'Split λογαριασμού με μερικές πληρωμές ανά προϊόν', 'Hybrid λειτουργία με τρίτους παρόχους', 'Room service σε ξενοδοχεία με χρέωση δωματίου', 'Χώροι υψηλής κίνησης με ημερήσια επαναφορά αρίθμησης'],
  },
  'third-party-integrations': {
    title: 'Ενσωματώσεις 3rd-Party',
    shortDescription: 'Συνδέστε efood και Wolt από ένα σημείο και κρατήστε μενού, παραγγελίες και status συγχρονισμένα.',
    overview: 'Λειτουργήστε κανάλια marketplaces χωρίς να αλλάζετε σύστημα. Ρυθμίστε integrations ανά τοποθεσία, λαμβάνετε παραγγελίες από partners και κρατήστε διπλό συγχρονισμό status για πιο ομαλή καθημερινή λειτουργία.',
    capabilities: [
      'Λήψη παραγγελιών από efood και Wolt webhooks με αντιστοίχιση ειδών μέσω SKU',
      'Συγχρονισμός τοπικών αλλαγών status πίσω στις πλατφόρμες',
      'Διαχείριση διαθεσιμότητας outlet στο efood (OPEN, CLOSED, CLOSED_UNTIL, CHECKIN)',
      'Αποθήκευση credentials ανά τοποθεσία για ασφαλή multi-branch λειτουργία',
      'Παρακολούθηση inbound/outbound integration events με structured logs',
      'Συγχρονισμός τοπικού μενού στο Wolt με dedicated flow',
    ],
    useCases: [
      'Εστιατόρια που συνδυάζουν direct και marketplace παραγγελίες',
      'Πολλαπλές τοποθεσίες με διαφορετικά partner credentials ανά branch',
      'Ομάδες που χρειάζονται ενιαίο dashboard για efood και Wolt',
      'Λειτουργίες που απαιτούν αξιόπιστο status alignment για λιγότερα support calls',
      'Brands που κλιμακώνουν delivery όγκο με σταθερή ροή κουζίνας',
    ],
  },
  'payments-and-tax-billing': {
    title: 'Πληρωμές & Φορολογική Τιμολόγηση',
    shortDescription: 'Εισπράξτε όπως σας βολεύει και διαχειριστείτε τη φορολογική ροή με σιγουριά.',
    overview: 'Κρατήστε πληρωμές και τιμολόγηση σε ένα σημείο, με ξεκάθαρη εικόνα για προσωπικό, πελάτη και λογιστήριο.',
    capabilities: [
      'Επιλογές πληρωμής online και στο κατάστημα',
      'Καθαρή επιβεβαίωση πληρωμών χωρίς ασάφεια',
      'Έλεγχος αυτόματης ή χειροκίνητης αποστολής τιμολόγησης',
      'Παρακολούθηση κατάστασης τιμολόγησης ανά παραγγελία',
      'Οργάνωση αποδείξεων και παραστατικών για το λογιστήριο',
    ],
    useCases: ['Συνδυασμός online και in-person πληρωμών', 'Ελληνικές επιχειρήσεις με myDATA', 'Ξενοδοχεία με χρέωση δωματίου', 'Ομάδες με manual έλεγχο τιμολόγησης', 'Χώροι με waiter-confirm και prepay flows'],
  },
  'pay-at-table': {
    title: 'Πληρωμή στο Τραπέζι',
    shortDescription: 'Δώστε τη δυνατότητα στον πελάτη να πληρώσει από το κινητό, χωρίς αναμονή για λογαριασμό.',
    overview: 'Επιταχύνετε το κλείσιμο τραπεζιών και βελτιώστε την εμπειρία εξόδου, ειδικά στις ώρες αιχμής.',
    capabilities: [
      'Πληρωμή υπολοίπου σε λίγα taps',
      'Μικρότερη αναμονή στο τέλος του γεύματος',
      'Καθαρή εικόνα πληρωμένου και υπολοίπου ποσού',
      'Ομαλή ένταξη στη ροή παραγγελίας σας',
      'Εμπειρία πληρωμής φιλική στο κινητό',
    ],
    useCases: ['Εστιατόρια που επιτρέπουν πληρωμή χωρίς αναμονή για λογαριασμό', 'Ξενοδοχεία με μετατροπή χρέωσης δωματίου σε online πληρωμή', 'Bar και cafe με εύκολη εξόφληση tab', 'Χώροι υψηλής κίνησης που μειώνουν τα trips σερβιτόρων'],
  },
  'multi-language-menus': {
    title: 'Πολυγλωσσικά Μενού',
    shortDescription: 'Εξυπηρετήστε διεθνείς πελάτες με μενού που καταλαβαίνουν άμεσα.',
    overview: 'Δείξτε το μενού σε πολλές γλώσσες και κάντε την παραγγελία πιο εύκολη για τουρίστες και ξένους επισκέπτες.',
    capabilities: [
      'Εμφάνιση μενού στις γλώσσες που χρειάζεται το κοινό σας',
      'Εναλλαγή γλώσσας από τον ίδιο τον πελάτη',
      'Γρήγορη μετάφραση περιεχομένου με δυνατότητα χειροκίνητων διορθώσεων',
      'Καθαρή παρουσίαση ονομάτων, περιγραφών και βασικών πληροφοριών',
      'Ένα ενιαίο μενού για πολλές γλωσσικές εκδόσεις',
    ],
    useCases: ['Τουριστικοί προορισμοί', 'Ξενοδοχεία με διεθνείς επισκέπτες', 'Αλυσίδες σε πολλές χώρες', 'Beach bars με diverse κοινό', 'Επιχειρήσεις που χρειάζονται άμεσες μεταφράσεις χωρίς μεταφραστή'],
  },
  'table-management': {
    title: 'Διαχείριση Τραπεζιών & Κλήση Σερβιτόρου',
    shortDescription: 'Οργανώστε καλύτερα το service με έξυπνη διαχείριση τραπεζιών και άμεση υποστήριξη πελατών.',
    overview: 'Βάλτε τάξη στη λειτουργία της σάλας με ξεκάθαρους κανόνες ανά περιοχή και πιο γρήγορη ανταπόκριση του προσωπικού.',
    capabilities: [
      'Γρήγορη δημιουργία και διαχείριση τραπεζιών ανά τοποθεσία',
      'Σωστή σύνδεση κάθε τραπεζιού με το κατάλληλο μενού',
      'Άμεση εικόνα διαθεσιμότητας τραπεζιών',
      'Έλεγχος ωρών παραγγελίας ανά ζώνη εξυπηρέτησης',
      'Κλήση προσωπικού από το μενού με ένα άγγιγμα',
    ],
    useCases: ['Εστιατόρια με υψηλή κίνηση', 'Μεγάλοι χώροι με ζώνες', 'Χώροι με διαφορετικές ώρες λειτουργίας (μεσημέρι + βράδυ)', 'Takeaway και self pick-up πάγκοι με ωράριο', 'Εξωτερικοί χώροι', 'Ξενοδοχεία με πολλαπλά service points'],
  },
  'restaurant-analytics': {
    title: 'Analytics Μενού',
    shortDescription: 'Δείτε τι πουλάει, πότε πουλάει και πού μπορείτε να αυξήσετε έσοδα.',
    overview: 'Μετατρέψτε την καθημερινή κίνηση σε αποφάσεις που βελτιώνουν πωλήσεις, εξυπηρέτηση και κερδοφορία.',
    capabilities: [
      'Παρακολούθηση πορείας πελάτη από προβολή έως παραγγελία',
      'Εντοπισμός top πιάτων και αδύναμων σημείων',
      'Εικόνα εσόδων ανά ημέρα, τραπέζι και seating area',
      'Ανάλυση ωρών αιχμής για σωστό προγραμματισμό προσωπικού',
      'Συγκρίσεις απόδοσης ανά περίοδο και τοποθεσία',
    ],
    useCases: ['Βελτιστοποίηση μενού και τιμών', 'Αξιολόγηση καμπανιών', 'Σύγκριση απόδοσης locations', 'Εντοπισμός χαμηλής απόδοσης προϊόντων'],
  },
  'restaurant-onboarding': {
    title: 'Onboarding Ροή Επιχείρησης',
    shortDescription: 'Ξεκινήστε γρήγορα με καθοδηγούμενο setup για ομάδες που δεν έχουν χρόνο για χάσιμο.',
    overview: 'Από την εγγραφή μέχρι το live μενού, όλα γίνονται βήμα-βήμα ώστε να ξεκινήσετε παραγγελίες όσο πιο σύντομα γίνεται.',
    capabilities: [
      'Απλή ροή εκκίνησης με καθοδήγηση σε κάθε βήμα',
      'Γρήγορη ρύθμιση στοιχείων επιχείρησης και τοποθεσίας',
      'Εφαρμογή λογοτύπου και εταιρικής εικόνας σε λίγα λεπτά',
      'Εισαγωγή υπάρχοντος μενού για εξοικονόμηση χρόνου',
      'Γρήγορο ανέβασμα πρώτου μενού και άμεση δημοσίευση',
    ],
    useCases: ['Νέα καταστήματα', 'Μετάβαση από έντυπα/Excel', 'Franchise onboarding', 'Pop-up events'],
  },
  'custom-branding': {
    title: 'Custom Branding Μενού',
    shortDescription: 'Κάντε το online μενού να δείχνει 100% δικό σας, από την πρώτη ματιά.',
    overview: 'Χτίστε μια premium εμπειρία που ταιριάζει στην ταυτότητα του χώρου σας και ενισχύει την εμπιστοσύνη του πελάτη.',
    capabilities: [
      'Εφαρμογή λογοτύπου, χρωμάτων και ύφους με λίγα βήματα',
      'Σταθερή εικόνα brand σε όλα τα σημεία επαφής',
      'Προσαρμογή εμφάνισης για πιο premium παρουσίαση',
      'Προεπισκόπηση πριν από τη δημοσίευση',
      'Διαφορετικό στυλ ανά μενού όπου χρειάζεται',
    ],
    useCases: ['Ταύτιση με εσωτερικό design', 'Πολλαπλά brands ανά όμιλο', 'Εποχιακά themes', 'Brand consistency σε franchise'],
  },
  'csv-import-export': {
    title: 'CSV Import & Export',
    shortDescription: 'Μεταφέρετε δεδομένα μενού γρήγορα και γλιτώστε ώρες χειροκίνητης δουλειάς.',
    overview: 'Φέρτε το υπάρχον μενού σας άμεσα στην πλατφόρμα και κρατήστε πάντα τον έλεγχο των δεδομένων σας.',
    capabilities: [
      'Μαζική εισαγωγή ειδών αντί για χειροκίνητη καταχώριση',
      'Εξαγωγή μενού για backup ή εσωτερικό έλεγχο',
      'Έτοιμα templates που μειώνουν τα λάθη μορφοποίησης',
      'Γρήγορες εποχιακές ή καμπανιακές ανανεώσεις',
      'Συνδυασμός με visual επεξεργασία για ταχύτητα και ακρίβεια',
    ],
    useCases: ['Μετάβαση από spreadsheets', 'Standardized rollouts', 'Εποχιακές πλήρεις αλλαγές', 'Backup/restore διαδικασίες'],
  },
  'employee-management': {
    title: 'Διαχείριση Προσωπικού & Αναφορές Βάρδιας',
    shortDescription: 'Συντονίστε την ομάδα καλύτερα με ξεκάθαρους ρόλους και καθημερινή εικόνα απόδοσης.',
    overview: 'Οργανώστε τη λειτουργία της ομάδας με σαφείς αναθέσεις και μετρήσιμα αποτελέσματα ανά βάρδια.',
    capabilities: [
      'Ανάθεση προσωπικού ανά περιοχή και βάρδια',
      'Εστιασμένη εικόνα αρμοδιοτήτων για κάθε εργαζόμενο',
      'Καθημερινές μετρήσεις απόδοσης που βγάζουν νόημα',
      'Γρήγορος έλεγχος βάρδιας για συμφωνίες',
      'Εντοπισμός δυνατών σημείων και αναγκών εκπαίδευσης',
    ],
    useCases: ['Αξιολόγηση σερβιτόρων', 'Αναθέσεις σε πολλαπλές ζώνες', 'Εναλλασσόμενες βάρδιες', 'Σύγκριση αποδοτικότητας προσωπικού'],
  },
  'kitchen-display-system': {
    title: 'Kitchen Display System (KDS)',
    shortDescription: 'Κρατήστε την κουζίνα συγχρονισμένη με καθαρές οθόνες, προτεραιότητες και πιο γρήγορα handoffs.',
    overview: 'Μειώστε λάθη και καθυστερήσεις με καλύτερη ορατότητα σε κάθε σταθμό προετοιμασίας.',
    capabilities: [
      'Εμφάνιση μόνο των σωστών ειδών στον σωστό σταθμό',
      'Επισήμανση επειγόντων παραγγελιών για σωστή προτεραιοποίηση',
      'Καθαρή παρακολούθηση ολοκλήρωσης ανά item',
      'Ομαλή συνεργασία κουζίνας και service',
      'Συνολική εικόνα όλων των ενεργών παραγγελιών',
    ],
    useCases: ['Κουζίνες με πολλαπλά stations', 'Bar/kitchen split', 'Room service operations', 'High-volume περιβάλλοντα'],
  },
  'customer-management': {
    title: 'Διαχείριση Πελατών',
    shortDescription: 'Χτίστε ισχυρότερη σχέση με τον πελάτη μέσω ιστορικού, προτιμήσεων και ταχύτερου reorder.',
    overview: 'Μετατρέψτε τις καθημερινές παραγγελίες σε πολύτιμη γνώση που βελτιώνει την εξυπηρέτηση και την πιστότητα.',
    capabilities: [
      'Καθαρό προφίλ και ιστορικό για κάθε πελάτη',
      'Γρήγορος εντοπισμός επιστρεφόντων πελατών',
      'Εικόνα για το ποιοι παραγγέλνουν και ξοδεύουν περισσότερο',
      'Ταχύτερο checkout με προσυμπληρωμένα στοιχεία',
      'Πιο προσωπική εμπειρία εξυπηρέτησης',
    ],
    useCases: ['Loyalty και repeat orders', 'Παρακολούθηση προτύπων επισκεπτών', 'Ταχύτερο checkout για regulars', 'Ανάλυση customer lifetime value'],
  },
  'inventory-tracking': {
    title: 'Παρακολούθηση Αποθέματος',
    shortDescription: 'Κρατήστε τον έλεγχο του στοκ και αποφύγετε απογοητευμένους πελάτες για μη διαθέσιμα είδη.',
    overview: 'Διατηρήστε το μενού σας πάντα ευθυγραμμισμένο με τη διαθεσιμότητα, ώστε ο πελάτης να βλέπει μόνο ό,τι μπορεί να παραγγείλει.',
    capabilities: [
      'Παρακολούθηση αποθέματος ανά είδος με απλό έλεγχο',
      'Γρήγορη ενημέρωση ποσοτήτων μέσα στη βάρδια',
      'Άμεση αντανάκλαση διαθεσιμότητας στο μενού',
      'Εύκολη επαναφορά διαθεσιμότητας όταν αλλάζει η παραγγελία',
      'Λιγότερα λάθη και αποφυγή overselling',
    ],
    useCases: ['Limited daily specials', 'Food trucks με περιορισμένο στοκ', 'Bakeries/cafes με fresh batches', 'Events με προκαθορισμένες μερίδες'],
  },
  'receipt-printing': {
    title: 'Εκτύπωση Αποδείξεων & Παραγγελιών',
    shortDescription: 'Εκτυπώστε αποδείξεις και tickets ακριβώς εκεί που τα χρειάζεται η ομάδα σας.',
    overview: 'Κάντε την εκτέλεση παραγγελιών πιο ομαλή, στέλνοντας το σωστό εκτυπωτικό αποτέλεσμα στο σωστό σημείο.',
    capabilities: [
      'Άμεση εκτύπωση αποδείξεων και εσωτερικών tickets',
      'Δρομολόγηση εκτυπώσεων ανά σημείο και ροή εργασίας',
      'Υποστήριξη τοπικής και cloud εκτύπωσης',
      'Αυτόματη εκτύπωση σε επιβεβαίωση παραγγελίας',
      'Λιγότερα χειροκίνητα βήματα για το προσωπικό',
    ],
    useCases: ['Εκτύπωση kitchen tickets κατά την επιβεβαίωση', 'Αποδείξεις ανά ταμείο/seating area', 'Πολλαπλές τοποθεσίες με cloud εκτύπωση', 'Ξενοδοχεία με διαφορετικούς εκτυπωτές ανά service point'],
  },
  'menu-widget-embed': {
    title: 'Widget Ενσωμάτωσης Μενού',
    shortDescription: 'Ενσωματώστε το live μενού στο site σας και κρατήστε το πάντα ενημερωμένο.',
    overview: 'Μετατρέψτε το website σας σε κανάλι παραγγελιών και προβολής, χωρίς να συντηρείτε δεύτερο μενού.',
    capabilities: [
      'Δημιουργία embed κώδικα σε λίγα δευτερόλεπτα',
      'Επιλογή μενού ανά brand ή τοποθεσία',
      'Προσαρμογή εμφάνισης ώστε να ταιριάζει στο site σας',
      'Προεπισκόπηση πριν τη δημοσίευση',
      'Αυτόματη ενημέρωση περιεχομένου χωρίς νέο embed',
    ],
    useCases: ['Εστιατόρια που θέλουν πάντα ενημερωμένο μενού στο site τους', 'Ξενοδοχεία με embedded room-service menu', 'Landing pages καμπανιών με ενσωματωμένο μενού', 'Agencies που διαχειρίζονται embeds για πολλούς πελάτες'],
  },
  'delivery-zones': {
    title: 'Ζώνες Delivery & Επαλήθευση Διεύθυνσης',
    shortDescription: 'Κάντε delivery μόνο όπου μπορείτε να εξυπηρετήσετε σωστά, με ξεκάθαρες ζώνες.',
    overview: 'Ορίστε ξεκάθαρα όρια εξυπηρέτησης ώστε ο πελάτης να γνωρίζει άμεσα αν καλύπτεται και η ομάδα να αποφεύγει προβληματικές παραγγελίες.',
    capabilities: [
      'Σχεδίαση και διαχείριση ζωνών delivery στον χάρτη',
      'Έλεγχος διεύθυνσης πριν την ολοκλήρωση παραγγελίας',
      'Καθαρή προβολή περιοχής εξυπηρέτησης για τον πελάτη',
      'Υποστήριξη delivery και self-pickup στην ίδια ροή',
      'Καλύτερος προγραμματισμός διαδρομών για την ομάδα',
    ],
    useCases: ['Εστιατόρια με delivery σε συγκεκριμένη ακτίνα', 'Ghost kitchens με ακριβείς ζώνες', 'Ξενοδοχεία με room service boundaries', 'Catering εταιρείες με επαλήθευση διεύθυνσης', 'Food trucks με δυναμική ζώνη delivery'],
  },
  'reservations-and-table-board': {
    title: 'Κρατήσεις & Live Πίνακας Τραπεζιών',
    shortDescription:
      'Δεχτείτε κρατήσεις με σιγουριά και διαχειριστείτε τη σάλα από έναν ζωντανό πίνακα που δείχνει κάθε τραπέζι, παραγγελία και κράτηση σε πραγματικό χρόνο.',
    overview:
      'Δώστε στο host stand τα εργαλεία να οργανώσει τη μέρα και στους managers μία μόνο οθόνη για να τρέξουν το service. Κάντε κρατήσεις με εγγυημένο κράτημα τραπεζιού, κλείστε τραπέζια για ιδιωτικές εκδηλώσεις και παρακολουθήστε τη σάλα να ενημερώνεται ζωντανά όσο έρχονται νέες παραγγελίες και αλλάζουν καταστάσεις — χωρίς ανανέωση σελίδας.',
    capabilities: [
      'Δημιουργία, επεξεργασία και ακύρωση κρατήσεων με όνομα πελάτη, αριθμό ατόμων, ώρα και προαιρετικά στοιχεία επικοινωνίας',
      'Δέσμευση ενός ή πολλών τραπεζιών ανά κράτηση, ή κλείσιμο τραπεζιών για ιδιωτικές εκδηλώσεις',
      'Αυτόματο παράθυρο κράτησης: το τραπέζι γίνεται "reserved" 30 λεπτά πριν την ώρα και ελευθερώνεται 2 ώρες μετά — όχι κρατημένο όλη μέρα',
      'Ειδική σελίδα "Κρατήσεις της ημέρας" με ταξινόμηση ανά ώρα και γρήγορη επεξεργασία / διαγραφή',
      'Live Table Board σε στυλ KDS: όλα τα τραπέζια ομαδοποιημένα ανά seating area με χρωματιστή κατάσταση (διαθέσιμο, κατειλημμένο, reserved, closed, ανενεργό)',
      'Ενημερώσεις σε πραγματικό χρόνο μέσω WebSocket — οι νέες παραγγελίες και οι αλλαγές κατάστασης εμφανίζονται αμέσως με οπτικό flash στο τραπέζι',
      'Άμεση αλλαγή κατάστασης από τον πίνακα: πατάτε ένα τραπέζι και το κάνετε available, occupied, reserved, closed ή inactive με ένα κλικ',
      'Λεπτομέρεια ανά τραπέζι με μία ματιά: ανοιχτές παραγγελίες της ημέρας με σύνολα, σερβιτόρο, κατάσταση πληρωμής, και τις σημερινές κρατήσεις που το αφορούν',
      'Συμπυκνωμένη μπάρα στατιστικών: σύνολα, διαθέσιμα / κατειλημμένα / reserved / closed, ανοιχτές παραγγελίες, pending κλήσεις σερβιτόρου και κρατήσεις σήμερα',
      'Φίλτρα ανά κατάσταση και ανά seating area, με ένδειξη "LIVE / OFFLINE" για την υγεία της σύνδεσης',
      'Η πρόσβαση στο online μενού και οι κλήσεις σερβιτόρου μπλοκάρονται αυτόματα για closed / inactive τραπέζια, ώστε να μη χάνεται τίποτα',
      'Ακρίβεια ώρας (Europe/Athens) — η ώρα που επιλέγετε είναι η ώρα που βλέπει ο πελάτης, χωρίς UTC εκπλήξεις',
    ],
    useCases: [
      'Εστιατόρια που δέχονται τηλεφωνικές κρατήσεις και θέλουν μία οθόνη με όλες τις σημερινές',
      'Fine-dining χώρους που θέλουν να κλείνουν συγκεκριμένα τραπέζια για ιδιωτικά events κρατώντας τα υπόλοιπα διαθέσιμα',
      'Πολυσύχναστα bistros όπου οι managers θέλουν άμεση εικόνα ανοιχτών παραγγελιών και κατάστασης τραπεζιών σε ένα dashboard',
      'Ξενοδοχεία και resorts με πολλαπλά seating areas (πισίνα, εστιατόριο, roof) που χρειάζονται ενιαίο floor control',
      'Ομάδες που κάνουν pre-service briefing: ποιοι έρχονται, τι ώρα και σε ποιο τραπέζι',
      'Προσωπικό που χειρίζεται walk-ins και θέλει άμεσο σημάδεμα occupied/available χωρίς να ψάχνει menus',
      'Ιδιοκτήτες που παρακολουθούν τη σάλα από το γραφείο ή το κινητό με live updates αντί για manual refresh',
    ],
  },
  'android-app': {
    title: 'Android App για Employees & Owners',
    shortDescription: 'Διαχειριστείτε την επιχείρηση από το κινητό με Android app για ιδιοκτήτες και προσωπικό.',
    overview: 'Μείνετε σε έλεγχο ακόμα και εκτός ταμείου, με άμεση εικόνα λειτουργίας και γρήγορη αντίδραση σε πραγματικό χρόνο.',
    capabilities: [
      'Διαχείριση παραγγελιών και καταστάσεων εν κινήσει',
      'Οργάνωση βαρδιών και καθημερινών εργασιών από ένα app',
      'Άμεση ανταπόκριση σε αιτήματα πελατών',
      'Παρακολούθηση τραπεζιών και κίνησης σε πραγματικό χρόνο',
      'Έλεγχος πωλήσεων και απόδοσης ομάδας όπου κι αν βρίσκεστε',
    ],
    useCases: ['Waiters on-the-go', 'Owners εκτός γραφείου', 'Floor management', 'Outdoor/event ομάδες'],
  },
};

export function localizeFeature(feature, language = 'en') {
  if (!feature || language !== 'el') return feature;
  const translated = FEATURE_TRANSLATIONS_EL[feature.slug];
  if (!translated) return feature;
  return {
    ...feature,
    ...translated,
  };
}
